/// <reference path="../../../ui/types.d.ts" />

import { ThemeProvider, createTheme } from '@mui/material';
import { withTheme } from '@rjsf/core';
import { generateTemplates, generateWidgets } from '@rjsf/mui';
import validator from '@rjsf/validator-ajv8';
import { useEffect, useRef, useState } from 'react';
import { createRoot } from 'react-dom/client';
import CrowdinFilesWidget from './crowdinFilesWidget';
import HtmlWidget from './htmlWidjet';
import MonacoEditorWidget from './monacoEditorWidget';
import JsonataEditorWidget from './jsonataEditorWidget';

declare let formPostDataUrl: string;
declare let formGetDataUrl: string;
declare let formPatchDataUrl: string;
declare let card: any;

const defaultAppearance = 'light';

const getAppearance = (crowdinAppearance: string) => {
    if (!['light', 'dark'].includes(crowdinAppearance)) {
        return defaultAppearance;
    }
    return crowdinAppearance;
};

function getPreparedTheme(mode: any) {
    return createTheme({
        palette: {
            mode,
        },
        ...(mode === 'dark'
            ? {
                  components: {
                      MuiTypography: {
                          styleOverrides: {
                              root: {
                                  color: 'rgba(255, 255, 255);',
                              },
                          },
                      },
                      MuiPaper: {
                          styleOverrides: {
                              root: {
                                  backgroundImage: 'none',
                                  backgroundColor: 'var(--crowdin-body-bg, #ffffff)',
                              },
                          },
                      },
                  },
              }
            : {}),
    });
}

const Form = withTheme({
    templates: generateTemplates(),
    widgets: {
        ...generateWidgets(),
        SelectWidget: (props) => {
            const OriginalWidget = generateWidgets().SelectWidget;

            return (
                <OriginalWidget
                    {...props}
                    SelectProps={{
                        ...(props.SelectProps || {}),
                        MenuProps: {
                            autoFocus: false,
                            disableAutoFocusItem: true,
                            disableEnforceFocus: true,
                            disableAutoFocus: true,
                        },
                    }}
                />
            );
        },
    },
});

function getId() {
    const context = frameData.app.context;
    const id = ['form-data'];
    if (context.organization_id) {
        id.push(context.organization_id);
    }
    if (context.project_id) {
        id.push(context.project_id);
    }

    return id.join('-');
}

function FormContainer({ formData, formSchema, formUiSchema, onFormRef }: any) {
    const [theme, setTheme] = useState<any>(null);
    const ref = useRef<any>();

    useEffect(() => {
        AP.events.on('theme.changed', (crowdinAppearance) => {
            const newAppearance = getAppearance(crowdinAppearance);
            setTheme(getPreparedTheme(newAppearance));
        });

        AP.getTheme((crowdinAppearance) => {
            const newAppearance = getAppearance(crowdinAppearance);
            setTheme(getPreparedTheme(newAppearance));
        });
    }, []);

    // we need this as we don't render anything while theme not set
    useEffect(() => {
        onFormRef(ref);
    }, [theme]);

    if (!theme) {
        return null;
    }

    const widgets = {
        htmlWidget: HtmlWidget,
        crowdinFilesWidget: CrowdinFilesWidget,
        monacoEditorWidget: (props: any) => <MonacoEditorWidget {...props} theme={theme} />,
        jsonataEditorWidget: (props: any) => <JsonataEditorWidget {...props} theme={theme} />,
    };

    return (
        <ThemeProvider theme={theme}>
            <Form
                ref={ref}
                widgets={widgets}
                formData={formData}
                validator={validator}
                schema={formSchema}
                uiSchema={formUiSchema}
                onSubmit={(e) => {
                    submitForm(e.formData);
                }}
                onChange={(e) => {
                    window.currentFormData = e.formData;

                    const myEvent = new CustomEvent('formDataUpdated', {
                        detail: {
                            ...e.formData,
                        },
                    });
                    document.dispatchEvent(myEvent);
                    if (AP.formDataUpdated) {
                        AP.formDataUpdated(e.formData);
                    }
                }}
            />
        </ThemeProvider>
    );
}

function removeBorders() {
    const card = document.getElementById('card') as any;
    card.style.border = 'none';
    card.style.overflow = 'initial';
    card.removeAttribute('is-shadowed');
    card.setAttribute('without-p', true);
    card.parentElement.style.padding = '16px';
    card.parentElement.style.paddingBottom = '0px';
}

function checkIfCurrentModuleCanSubmitForm() {
    const modulesWithoutSubmit = ['ai-prompt-provider', 'workflow-step-type', 'automation-action'];
    return !(AP && AP._data && AP._data.app && modulesWithoutSubmit.includes(AP._data.app.type));
}

function renderForm(formData: any = {}, formSchema?: any, formUiSchema?: any) {
    const root = createRoot(document.getElementById('form') as any);
    const modulesWithoutBorders = ['modal', 'editor-right-panel'];

    if (AP && AP._data && AP._data.app && AP._data.app.type) {
        const htmlElement = document.getElementsByTagName('html')[0];
        htmlElement.className += ' ' + AP._data.app.type + '-form';
    }

    if (!checkIfCurrentModuleCanSubmitForm()) {
        removeBorders();
        const htmlElement = document.getElementsByTagName('html')[0];
        htmlElement.className += ' form-without-submit';
    }

    if (AP && AP._data && AP._data.app && modulesWithoutBorders.includes(AP._data.app.type)) {
        removeBorders();
        if (AP._data.app.type === 'modal') {
            card.parentElement.style.removeProperty('max-width');
        }
    }

    window.currentFormSchema = formSchema;
    window.currentFormData = formData;
    window.currentFormUiSchema = formUiSchema;

    root.render(
        <FormContainer
            onFormRef={(ref: any) => {
                window.formRef = ref.current;
            }}
            formData={formData}
            formSchema={formSchema || window.formSchema}
            formUiSchema={formUiSchema || window.formUiSchema}
        />,
    );
    (document.getElementById('form-loading') as any).style.display = 'none';
}
function submitForm(formData: any) {
    if (!checkIfCurrentModuleCanSubmitForm()) {
        return;
    }

    (document.getElementById('form-loading') as any).style.display = 'flex';
    checkOrigin()
        .then((restParams) =>
            fetch(formPostDataUrl + restParams + '&id=' + getId(), {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ data: formData, context: frameData.app.context }),
            }),
        )
        .then(checkResponse)
        .then((response) => {
            if (AP._data.app.type === 'modal' && !response?.message) {
                AP.closeAppModal();
                return;
            }

            if (!response || typeof response.message === 'undefined') {
                showToast('Settings successfully saved');
            }
            (document.getElementById('form-loading') as any).style.display = 'none';
            if (response && (response.formData || response.formSchema || response.formUiSchema)) {
                renderForm(response.formData, response.formSchema, response.formUiSchema);
            }

            if (response && response.redirect) {
                window.location = response.redirect;
            }
        })
        .catch((e) => {
            catchRejection(e, "Can't save settings");
            (document.getElementById('form-loading') as any).style.display = 'none';
        });
}

function getSettings() {
    return checkOrigin()
        .then((restParams) => {
            const context = new URLSearchParams(frameData.app.context).toString();
            const params = new URLSearchParams(restParams + '&id=' + getId());
            params.set('context', context);

            return fetch(formGetDataUrl + '?' + params.toString(), {
                method: 'GET',
            });
        })
        .then(checkResponse)
        .catch((e) => catchRejection(e, "Can't load saved settings"));
}

function patchSettings(formData: any, renderData: any) {
    return checkOrigin()
        .then((restParams) => {
            return fetch(formPatchDataUrl + restParams, {
                method: 'PATCH',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ data: { formData, renderData }, context: frameData.app.context }),
            });
        })
        .then(checkResponse)
        .catch((e) => catchRejection(e, "Can't patch form data"));
}

if (checkIfCurrentModuleCanSubmitForm()) {
    getSettings()
        .then((response) => {
            renderForm(response.formData, response.formSchema, response.formUiSchema);
        })
        .catch(() => renderForm({} as any));
} else {
    AP.getFormData((formData) => {
        if (formPatchDataUrl) {
            if (AP.getRenderData && typeof AP.getRenderData === 'function') {
                AP.getRenderData((renderData) => {
                    patchSettings(formData, renderData)
                        .then((response) => {
                            renderForm(response.formData, response.formSchema, response.formUiSchema);
                        })
                        .catch(() => renderForm({}));
                });
            } else {
                // Call patchSettings with null renderData when getRenderData is not available
                patchSettings(formData, null)
                    .then((response) => {
                        renderForm(response.formData, response.formSchema, response.formUiSchema);
                    })
                    .catch(() => renderForm({}));
            }
        } else {
            renderForm(formData);
        }
    });
}

window.renderForm = renderForm;
